<template>
  <Card :footerDivider="false" title="">
    <template v-slot:content>
      <div>
        <div class="alert-box">
          <div class="alert-text">
            <Alert :size="alertSize" :text="alertText" />
          </div>
          <div class="alert-space hide-mobile"></div>
          <div class="alert-button hide-mobile">
            <CustomButton
              v-if="showRecalculateButton"
              :buttonColor="ButtonColor.TERTIARY"
              :disabledState="!isCalculateButtonEnabled"
              :pendingState="loading"
              :showRefreshSpinner="true"
              class="btnRecalculate"
              @click="updatePremium"
            >
              {{ formSubmitButtonLabel }}
            </CustomButton>
            <CustomButton
              v-if="!showRecalculateButton"
              :buttonColor="ButtonColor.SECONDARY"
              class="btnPremium"
              >{{ getPrintPremium() }}
            </CustomButton>
            <small
              v-if="!showRecalculateButton && premiumSubtext"
              class="premiumSubtext mt-8 centerHeight"
            >
              {{ premiumSubtext
              }}<span class="icon-vorteile" @click="openModalProlongation"></span>
            </small>
          </div>
        </div>
        <p
          v-if="errorText"
          class="form-group__error mt-32 mb-32"
          v-dompurify-html="errorText"
        ></p>
      </div>
    </template>

    <template v-slot:footer>
      <div class="footer-cta footer-cta--left">
        <div class="button-container">
          <CustomButton
            v-if="showRecalculateButton"
            :buttonColor="ButtonColor.TERTIARY"
            :disabledState="!isCalculateButtonEnabled"
            :pendingState="loading"
            :showRefreshSpinner="true"
            class="hide-desktop btnRecalculate"
            @click="updatePremium"
          >
            {{ formSubmitButtonLabel }}
          </CustomButton>
          <CustomButton
            v-if="!showRecalculateButton"
            :buttonColor="ButtonColor.PRIMARY"
            class="hide-desktop btnPremium"
            >{{ getPrintPremium() }}
          </CustomButton>
          <div
            v-if="!showRecalculateButton && premiumSubtext"
            class="hide-desktop premiumSubtextMobile"
          >
            {{ premiumSubtext }}
            <span class="icon-vorteile" @click="openModalProlongation"></span>
          </div>
          <CustomButton
            :buttonColor="ButtonColor.SECONDARY"
            class="btnResetData mobile-style"
            @click="resetData"
          >
            {{ formAbortButtonLabel }}
          </CustomButton>
        </div>
        <Modal
          v-if="modal"
          :centeredText="false"
          :maxWidth="600"
          :headerDivider="false"
          showCloseIcon
          :showModal="showModalProlongation"
          :title="modal.title"
          @closeModal="closeModalProlongation"
        >
          <template v-slot:content>
            <div v-dompurify-html="modal.text"></div>
            <!-- //NOSONAR -->
          </template>
        </Modal>
      </div>
    </template>
  </Card>
</template>

<script lang="ts" setup>
import {
  Alert,
  AlertSize,
  ButtonColor,
  Card,
  CustomButton,
  Modal
} from 'de-zurich-ui-library'
import { onMounted, type PropType, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { type MotorContractCoverageChangeRequest } from '@/typesTenant/MotorContractChange'
import { printPremium } from '@/utilsTenant/MotorUtils'
import { computePriceForContractChange } from '@/storeTenant/motorServiceTenant'

const props = defineProps({
  motorContractCoverageChangeRequest: {
    type: Object as PropType<MotorContractCoverageChangeRequest>,
    required: true
  },
  isCalculateButtonEnabled: {
    type: Boolean,
    required: true
  },
  showRecalculateButton: {
    type: Boolean,
    required: true
  },
  paymentPeriod: {
    type: String,
    required: true
  },
  premiumSubtext: {
    type: String,
    required: true
  },
  modal: {
    type: Object as PropType<Record<string, string>>,
    required: true
  }
})

const { t } = useI18n()

const errorText = ref('')
const loading = ref(false)
const premium = ref(0)
const formSubmitButtonLabel = ref('')
const formAbortButtonLabel = ref('')
const alertText = ref('')
const textReady = ref('')
const alertSize = ref(AlertSize.SMALL)
const showModalProlongation = ref(false)

const emit = defineEmits<{
  resetData: [none: void]
  updatePremium: [value: number]
}>()

onMounted(() => {
  formSubmitButtonLabel.value = t('calculatePremium.calculatePremium')
  formAbortButtonLabel.value = t('calculatePremium.resetData')
  alertText.value = t('calculatePremium.textStart')
  textReady.value = t('calculatePremium.textReady')
})

function closeModalProlongation() {
  showModalProlongation.value = false
}

function openModalProlongation() {
  showModalProlongation.value = true
}

function getPrintPremium(): string {
  return props.premiumSubtext
    ? `${printPremium(premium.value)}*`
    : `${printPremium(premium.value)}`
}

watch(
  () => [props.showRecalculateButton, props.isCalculateButtonEnabled],
  () => {
    if (props.showRecalculateButton && props.isCalculateButtonEnabled) {
      alertText.value = textReady.value
    }
  }
)

function resetData(): void {
  premium.value = 0
  alertText.value = t('calculatePremium.textStart')
  emit('resetData')
}

async function updatePremium(): Promise<void> {
  loading.value = true
  errorText.value = ''

  try {
    premium.value = await computePriceForContractChange(
      props.motorContractCoverageChangeRequest
    )
    alertText.value =
      t('calculatePremium.textCalculated') +
      ' ' +
      t(`paymentPeriod2.${props.paymentPeriod}`) +
      ' ' +
      t('calculatePremium.textCalculated2')
    emit('updatePremium', premium.value)
  } catch (_error) {
    errorText.value = t('calculatePremium.priceCalculationRequestErrorText')
  } finally {
    loading.value = false
  }
}
</script>

<style lang="scss" scoped>
.button-container {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  button {
    margin: 0 !important;
  }
}

.alert-box {
  display: flex;
  margin-bottom: 16px;
}

.alert-box > div {
  display: inline-flex;
}

.btnResetData {
  padding: 0 32px !important;
}

:deep(.alert) {
  width: 100%;
}

.alert-button :deep(button.btnPremium) {
  cursor: default !important;
}

.alert-text {
  width: 100%;
}

.alert-space {
  width: 0;
}

.alert-button {
  width: 100%;
}

.centerHeight {
  display: flex;
  align-items: center;
}

.form-group__error {
  margin-left: calc(var(--baseline) * 2);
  margin-bottom: var(--baseline);
}

.premiumSubtext {
  position: absolute;
  top: 46px;
}

.premiumSubtextMobile {
  position: relative;
}

@media (min-width: 600px) {
  .alert-box {
    margin-bottom: calc(var(--baseline) * 1);
  }
}

@media (min-width: 800px) {
  .alert-space {
    width: 14px;
  }

  .alert-button {
    width: 280px;
  }
}

@media (min-width: 1024px) {
  .alert-button {
    min-width: 215px;
  }
}

@media (min-width: 800px) {
  .hide-desktop {
    display: none !important;
  }
}

@media (max-width: calc(799px)) {
  .hide-mobile {
    display: none !important;
  }
}
</style>
